import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';

import Button from 'components/DesignSystem/Button';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import ImageColumns from 'components/Meteorology/Shared/ImageColumns';
import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import { DisplayImage } from 'helper/products/meteorology';
import {
  selectMFClassicComparatorBaseModel,
  selectMFClassicComparatorConfrontingModel,
  selectMFClassicComparatorVariable,
} from 'redux/reducers/meteorologyFieldsClassicComparatorControlPanelReducer';
import { ResponseError } from 'services/api/base';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import ControlPanelSheet from './ControlPanelSheet';

import './index.scss';

export interface ClassicComparatorMobileProps {
  showInstructions: boolean;
  imageSets: DisplayImage[][];
  columnLabels: string[];
  rowLabels: string[];
  onDownloadClick: () => void;
  downloadDisabled: boolean;
  isFetching: boolean;
  queryError: ResponseError | undefined;
  onRetry: () => void;
}

export default function ClassicComparatorMobile({
  showInstructions,
  imageSets,
  columnLabels,
  rowLabels,
  onDownloadClick,
  downloadDisabled,
  isFetching,
  queryError,
  onRetry,
}: ClassicComparatorMobileProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const { variables, getModelOptions } = useControlPanelOptions('comparador');

  const variable = useSelector(selectMFClassicComparatorVariable);
  const baseModel = useSelector(selectMFClassicComparatorBaseModel);
  const confrontingModel = useSelector(selectMFClassicComparatorConfrontingModel);

  const variableLabel = variables.find((v) => v.id === variable)?.label;
  // Modelos são exibidos em caixa alta no desktop (`text-uppercase` no ModelAccordion).
  const confrontingLabel = getModelOptions(confrontingModel).model.label.toUpperCase();
  const baseLabel = getModelOptions(baseModel).model.label.toUpperCase();
  const modelsLabel = confrontingLabel && baseLabel
    ? `${confrontingLabel} vs ${baseLabel}`
    : undefined;

  return (
    <div className="cc-mobile">
      <QueryCard>
        {variableLabel && (
          <QueryCardHeader
            label="Consulta atual"
            title={variableLabel}
            subtitle={modelsLabel}
            action={(
              <Button
                icon="download"
                type="tertiary"
                small
                title="Baixar imagens"
                onClick={onDownloadClick}
                disabled={downloadDisabled}
              />
            )}
          />
        )}
        <QueryCardTrigger
          onClick={() => setPanelOpen(true)}
          hint="Variável · Modelos · Período"
        />
      </QueryCard>

      {showInstructions && (
        <div className="cc-mobile-instructions">
          <p className="cc-mobile-instructions-title">Configure e realize a consulta</p>
          <p className="cc-mobile-instructions-body">
            Escolha a variável, os dois modelos e o período de análise no painel de
            controle para comparar as imagens.
          </p>
          <button
            type="button"
            className="cc-mobile-instructions-cta"
            onClick={() => setPanelOpen(true)}
          >
            Abrir painel de controle
          </button>
        </div>
      )}

      {!showInstructions && queryError && (
        <QueryErrorState
          title={queryError.data}
          message={queryError.description}
          onRetry={onRetry}
        />
      )}

      {!showInstructions && !queryError && isFetching && (
        <div className="cc-mobile-loading">
          <Spinner />
        </div>
      )}

      {!showInstructions && !queryError && !isFetching && (
        <ImageColumns
          imageSets={imageSets}
          columnLabels={columnLabels}
          rowLabels={rowLabels}
        />
      )}

      <ControlPanelSheet open={panelOpen} dismiss={() => setPanelOpen(false)} />
    </div>
  );
}
